/* Number / date formatting · Norwegian-flavoured but English labels. */

const nf0 = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });

export const n0 = (v: number): string => nf0.format(v);
export const n1 = (v: number): string => nf1.format(v);

/** Adult-female lice per fish. Always two decimals.
 *
 * Not a style choice: two decimals is exactly the precision BarentsWatch
 * reports. Fewer digits invent agreement -- n1(0.25) renders "0.3" beside a
 * reason string that says "0.25", and 0.15 showed as "0.2" on the location page
 * while the same figure read "0.15" on the cohort page. More digits would
 * invent precision the underlying fish count does not support.
 */
export const lice = (v: number): string => v.toFixed(2);

/** Compact tonnes/kt. <10 kt shows kt with one decimal, else whole kt. */
export function tonnes(t: number): { value: string; unit: string } {
  if (t >= 1000) {
    const kt = t / 1000;
    return { value: kt >= 10 ? n0(kt) : n1(kt), unit: "kt" };
  }
  return { value: n0(t), unit: "t" };
}

/** NOK money in MNOK / NOK bn. */
export function money(nok: number): { value: string; unit: string } {
  const m = nok / 1e6;
  if (m >= 1000) return { value: n1(m / 1000), unit: "NOK bn" };
  return { value: n0(m), unit: "MNOK" };
}

export function pct(v: number, digits = 0): string {
  return (digits ? n1(v * 100) : n0(v * 100)) + "%";
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function monthYear(month: number | null, year: number | null): string {
  if (!month || !year) return "·";
  return `${MONTHS[month - 1]} ${year}`;
}

/** "wk 23 · 2026" from a packed ISO yearweek integer like 202623. */
export function isoWeekLabel(packed: number | string): string {
  const s = String(packed);
  if (s.length < 5) return s;
  const year = s.slice(0, 4);
  const week = s.slice(4);
  return `wk ${Number(week)} · ${year}`;
}

/** Human "X days/hours ago" from an ISO timestamp, anchored to a given now. */
export function ago(iso: string, now: Date): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "·";
  const mins = Math.max(0, Math.round((now.getTime() - t) / 60000));
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 36) return `${hrs} h ago`;
  const days = Math.round(hrs / 24);
  return `${days} d ago`;
}

/** "12 Jun 2026" from an ISO date / date-time string. "·" if unparseable. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return "·";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "·";
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export function titleCase(s: string): string {
  // Unicode-aware: capitalise the first letter of each word only. The old
  // \b\w approach treated æ/ø/å as word boundaries (e.g. "Rødøy" -> "RøDøY").
  return s
    .toLowerCase()
    .replace(/(^|[\s\-/(.])(\p{L})/gu, (_, sep: string, ch: string) => sep + ch.toUpperCase())
    .replace(/\bAs\b/g, "AS");
}
